/**
 * ApplicationsTab Component
 * Displays and manages all applications in the admin dashboard
 * With search and filter capabilities
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Trash2, Filter, Users, FileText, Loader2, AlertTriangle, Search, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { reportsAPI, downloadCSV } from '@/lib/api';

const JOURNEY_STAGES = [
  { value: 'all', label: 'All Stages' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'lead_validated', label: 'Lead Validated' },
  { value: 'application_received', label: 'App Received' },
  { value: 'application_submitted', label: 'App Submitted' },
  { value: 'offer_issued', label: 'Offer Issued' },
  { value: 'offer_accepted', label: 'Offer Accepted' },
  { value: 'uco', label: 'UCO' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'cas_stage', label: 'CAS Stage' },
  { value: 'refund', label: 'Refund' },
  { value: 'visa_issued', label: 'Visa Issued' },
  { value: 'enrolment_completed', label: 'Enrolled' },
  { value: 'commission_received', label: 'Commission' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending Review' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'visa_applied', label: 'Visa Applied' },
  { value: 'visa_successful', label: 'Visa Successful' },
  { value: 'visa_rejected', label: 'Visa Rejected' },
  { value: 'disappeared', label: 'Disappeared' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'direct', label: 'Direct (B2C)' },
  { value: 'agent', label: 'Via Agent (B2B)' },
];

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    draft: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    submitted: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    under_review: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    accepted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    enrolled: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
    visa_applied: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
    visa_successful: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    visa_rejected: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
    disappeared: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300',
  };
  return colors[status] || colors.pending;
};

export const ApplicationsTab = ({ applications, onRefresh, user, initialJourneyStage = null }) => {
  const navigate = useNavigate();
  const [journeyStageFilter, setJourneyStageFilter] = useState(initialJourneyStage || 'all');
  // When a stage is applied via deep-link (funnel widget), match "stage or later"
  // so cumulative counts align with what the funnel displayed. Toggled off the
  // first time the user changes the dropdown manually.
  const [stageAtOrAfter, setStageAtOrAfter] = useState(!!initialJourneyStage);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('all');
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingApplication, setDeletingApplication] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    studentName: '',
    studentId: '',
    counselorName: '',
    courseName: '',
    universityName: ''
  });

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const universities = [...new Set(applications.map(a => a.university_name).filter(Boolean))].sort();
    const agents = [...new Set(applications.map(a => a.agent_name).filter(Boolean))].sort();
    return { universities, agents };
  }, [applications]);

  const filteredApplications = useMemo(() => {
    // Ordered list of journey stages for "stage-or-later" comparisons (matches
    // the backend's ADMIN_JOURNEY_STAGES ordering + legacy status aliases).
    const STAGE_ORDER = [
      'inquiry', 'lead_validated', 'application_received', 'application_submitted',
      'offer_issued', 'offer_accepted', 'uco', 'deposit',
      'cas_stage', 'refund', 'visa_issued', 'enrolment_completed', 'commission_received',
    ];
    const LEGACY_STAGE_ALIAS = {
      // legacy status values map into the ordered timeline
      pending: 'application_received',
      draft: 'application_received',
      submitted: 'application_submitted',
      under_review: 'application_submitted',
      offer_received: 'offer_issued',
      conditional_offer: 'offer_issued',
      unconditional_offer: 'offer_issued',
      accepted: 'offer_accepted',
      cas_issued: 'cas_stage',
      visa_applied: 'cas_stage',
      enrolled: 'enrolment_completed',
      completed: 'enrolment_completed',
    };
    const stageIdx = (raw) => {
      if (!raw) return -1;
      const canonical = LEGACY_STAGE_ALIAS[raw] || raw;
      return STAGE_ORDER.indexOf(canonical);
    };
    const cutoffIdx = stageAtOrAfter && journeyStageFilter !== 'all'
      ? stageIdx(journeyStageFilter)
      : -1;

    return applications.filter(app => {
      // Journey-stage filter
      if (journeyStageFilter !== 'all') {
        if (stageAtOrAfter && cutoffIdx >= 0) {
          const appIdx = Math.max(stageIdx(app.journey_stage), stageIdx(app.status));
          if (appIdx < cutoffIdx) return false;
        } else {
          if (app.journey_stage !== journeyStageFilter) return false;
        }
      }
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      if (sourceTypeFilter !== 'all') {
        if (sourceTypeFilter === 'direct' && app.agent_id) return false;
        if (sourceTypeFilter === 'agent' && !app.agent_id) return false;
      }
      if (needsReviewOnly && !app.needs_review) return false;
      
      // Quick search (searches across all fields)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          (app.reference_code || '').toLowerCase().includes(search) ||
          (app.student_name || '').toLowerCase().includes(search) ||
          (app.student_email || '').toLowerCase().includes(search) ||
          (app.id || '').toLowerCase().includes(search) ||
          (app.student_id || '').toLowerCase().includes(search) ||
          (app.course_name || '').toLowerCase().includes(search) ||
          (app.university_name || '').toLowerCase().includes(search) ||
          (app.agent_name || '').toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Advanced filters
      if (advancedFilters.studentName && 
          !(app.student_name || '').toLowerCase().includes(advancedFilters.studentName.toLowerCase())) {
        return false;
      }
      if (advancedFilters.studentId && 
          !((app.id || '').toLowerCase().includes(advancedFilters.studentId.toLowerCase()) ||
            (app.student_id || '').toLowerCase().includes(advancedFilters.studentId.toLowerCase()))) {
        return false;
      }
      if (advancedFilters.counselorName && 
          !(app.agent_name || '').toLowerCase().includes(advancedFilters.counselorName.toLowerCase())) {
        return false;
      }
      if (advancedFilters.courseName && 
          !(app.course_name || '').toLowerCase().includes(advancedFilters.courseName.toLowerCase())) {
        return false;
      }
      if (advancedFilters.universityName && 
          !(app.university_name || '').toLowerCase().includes(advancedFilters.universityName.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [applications, journeyStageFilter, statusFilter, sourceTypeFilter, needsReviewOnly, searchTerm, advancedFilters, stageAtOrAfter]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setJourneyStageFilter('all');
    setStatusFilter('all');
    setSourceTypeFilter('all');
    setAdvancedFilters({
      studentName: '',
      studentId: '',
      counselorName: '',
      courseName: '',
      universityName: ''
    });
  };

  const hasActiveFilters = searchTerm || 
    journeyStageFilter !== 'all' || 
    statusFilter !== 'all' || 
    sourceTypeFilter !== 'all' ||
    Object.values(advancedFilters).some(v => v);

  const openDeleteConfirm = (app, e) => {
    e.stopPropagation();
    setDeletingApplication(app);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingApplication) return;
    setIsDeleting(true);
    try {
      await api.delete(`/admin/applications/${deletingApplication.id}`);
      toast.success('Application deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingApplication(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
      {/* Search and Filter Section */}
      <div className="space-y-4 mb-6">
        {/* Quick Search Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by ref code, student name, ID, course, university, or agent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600"
              data-testid="application-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`${showAdvancedFilters ? 'bg-teal-50 border-teal-300 text-teal-700 dark:bg-teal-900/30 dark:border-teal-700 dark:text-teal-400' : ''}`}
            data-testid="toggle-advanced-filters"
          >
            <Filter className="w-4 h-4 mr-2" />
            Advanced Filters
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearAllFilters}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              data-testid="clear-all-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Filter by specific fields:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Student Name</label>
                <Input
                  placeholder="Enter name..."
                  value={advancedFilters.studentName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, studentName: e.target.value }))}
                  className="bg-white dark:bg-slate-700 text-sm"
                  data-testid="filter-student-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Application/Student ID</label>
                <Input
                  placeholder="Enter ID..."
                  value={advancedFilters.studentId}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, studentId: e.target.value }))}
                  className="bg-white dark:bg-slate-700 text-sm"
                  data-testid="filter-student-id"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Counselor/Agent Name</label>
                <Input
                  placeholder="Enter name..."
                  value={advancedFilters.counselorName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, counselorName: e.target.value }))}
                  className="bg-white dark:bg-slate-700 text-sm"
                  data-testid="filter-counselor-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Course Name</label>
                <Input
                  placeholder="Enter course..."
                  value={advancedFilters.courseName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, courseName: e.target.value }))}
                  className="bg-white dark:bg-slate-700 text-sm"
                  data-testid="filter-course-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">University Name</label>
                <Input
                  placeholder="Enter university..."
                  value={advancedFilters.universityName}
                  onChange={(e) => setAdvancedFilters(prev => ({ ...prev, universityName: e.target.value }))}
                  className="bg-white dark:bg-slate-700 text-sm"
                  data-testid="filter-university-name"
                />
              </div>
            </div>
          </div>
        )}

        {/* Header with dropdown filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">All Applications</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredApplications.length === applications.length 
                ? `${applications.length} total applications` 
                : `Showing ${filteredApplications.length} of ${applications.length} applications`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              variant={needsReviewOnly ? 'default' : 'outline'}
              onClick={() => setNeedsReviewOnly(v => !v)}
              className={needsReviewOnly ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'border-rose-300 text-rose-600 hover:bg-rose-50'}
              data-testid="filter-needs-review"
              title="Show only applications flagged as high-risk by the visa predictor"
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Needs review
            </Button>
            <div className="flex flex-col gap-1">
              <Select value={journeyStageFilter} onValueChange={(v) => { setJourneyStageFilter(v); setStageAtOrAfter(false); }}>
                <SelectTrigger className="w-44 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600" data-testid="journey-stage-filter">
                  <SelectValue placeholder="Journey Stage" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 max-h-80">
                  {JOURNEY_STAGES.map(stage => (
                    <SelectItem key={stage.value} value={stage.value} className="text-gray-800 dark:text-white">
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {stageAtOrAfter && journeyStageFilter !== 'all' && (
                <span className="text-[10px] text-teal-600 dark:text-teal-400" data-testid="stage-at-or-after-hint">
                  Showing this stage &amp; later (via funnel)
                </span>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                {STATUS_OPTIONS.map(status => (
                  <SelectItem key={status.value} value={status.value} className="text-gray-800 dark:text-white">
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceTypeFilter} onValueChange={setSourceTypeFilter}>
              <SelectTrigger className="w-36 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600" data-testid="source-type-filter">
                <Users className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                {SOURCE_OPTIONS.map(source => (
                  <SelectItem key={source.value} value={source.value} className="text-gray-800 dark:text-white">
                    {source.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info('Generating applications report...');
                  const params = {};
                  if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
                  await downloadCSV(reportsAPI.exportApplications(params), `applications_report_${new Date().toISOString().slice(0,10)}.csv`);
                  toast.success('Applications report downloaded');
                } catch (err) {
                  const msg = err?.message || 'Failed to download report';
                  toast.error(msg);
                }
              }}
              className="gap-2"
              data-testid="download-applications-csv"
            >
              <Download className="w-4 h-4" />
              <span className="hidden lg:inline">Export CSV</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-600">
              <th className="text-center py-3 px-2 text-gray-500 dark:text-gray-400 font-medium w-10" title="Primary application per student">★</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Ref Code</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Student</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Course</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Journey Stage</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Status</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Agent</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Applied</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.length === 0 ? (
              <tr>
                <td colSpan="9" className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No applications match your filters</p>
                </td>
              </tr>
            ) : (
              filteredApplications.map((app) => (
                <tr 
                  key={app.id} 
                  className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                  onClick={() => window.location.href = `/admin/applications/${app.id}`}
                  data-testid={`admin-app-row-${app.id}`}
                >
                  <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const res = await api.patch(`/applications/${app.id}/primary`);
                          app.is_primary = res.data.is_primary;
                          if (onRefresh) onRefresh();
                        } catch (err) {
                          console.error('Failed to toggle primary', err);
                        }
                      }}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-lg leading-none transition-all ${
                        app.is_primary
                          ? 'text-amber-500 hover:text-amber-600'
                          : 'text-gray-300 hover:text-amber-400 dark:text-gray-600 dark:hover:text-amber-400'
                      }`}
                      title={app.is_primary ? 'Primary application for this student' : 'Mark as primary'}
                      data-testid={`primary-toggle-${app.id}`}
                    >
                      {app.is_primary ? '★' : '☆'}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {app.reference_code || `${app.id?.slice(0, 8)}…`}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400/20 to-teal-600/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-teal-600 dark:text-teal-400 font-semibold text-sm">
                          {app.student_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium text-gray-800 dark:text-white">{app.student_name}</p>
                          {app.needs_review && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 text-[10px] font-medium border border-rose-300"
                              title={app.needs_review_reason || 'High-risk profile flagged by the visa predictor'}
                              data-testid={`needs-review-badge-${app.id}`}
                            >
                              <AlertTriangle className="w-2.5 h-2.5" /> Needs review
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">{app.student_email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-gray-800 dark:text-white text-sm font-medium truncate max-w-[200px]">{app.course_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{app.university_name}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs">
                      {(app.journey_stage || 'inquiry').replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={getStatusColor(app.status)}>
                      {app.status?.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    {app.agent_name ? (
                      app.agent_id ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin?tab=agents&agentId=${app.agent_id}`);
                          }}
                          className="text-purple-600 dark:text-purple-400 text-sm hover:underline focus:outline-none focus:underline text-left max-w-[180px] truncate"
                          title={`View ${app.agent_name}'s profile`}
                          data-testid={`app-agent-link-${app.id}`}
                        >
                          {app.agent_name}
                        </button>
                      ) : (
                        <span className="text-purple-600 dark:text-purple-400 text-sm">{app.agent_name}</span>
                      )
                    ) : (
                      <span className="text-gray-400 text-sm">Direct</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-gray-500 dark:text-gray-400 text-sm">
                    {new Date(app.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/admin/applications/${app.id}`;
                        }}
                        className="bg-teal-600/10 text-teal-600 hover:bg-teal-600/20 dark:bg-teal-500/20 dark:text-teal-400"
                        data-testid={`view-app-${app.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {user?.is_super_admin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => openDeleteConfirm(app, e)}
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          data-testid={`delete-app-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Application Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Application
            </DialogTitle>
          </DialogHeader>
          {deletingApplication && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to delete the application for <strong>{deletingApplication.course_name}</strong> by <strong>{deletingApplication.student_name}</strong>?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                  data-testid="confirm-delete-app"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationsTab;
